import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Loads and manages the PlayHQ text overrides map.
 *
 * The map is `{ path: value }` where `path` is a dotted key (e.g.
 * `page.results.title` or `game.<id>.opponent`). Admins can call
 * `save(path, value)` to persist an override on the backend; the value is
 * mirrored into the local map so re-renders show the update immediately.
 *
 * Everyone (member, admin, public) fetches the map on mount so the UI can
 * transparently apply overrides without any auth check.
 */
export function useOverrides() {
  const [map, setMap] = useState({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/playhq/overrides");
      setMap(data || {});
    } catch {
      setMap({});
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const get = useCallback((path, fallback) => {
    if (path && Object.prototype.hasOwnProperty.call(map, path)) return map[path];
    return fallback;
  }, [map]);

  const save = useCallback(async (path, value) => {
    if (!path) return;
    try {
      await api.put("/playhq/overrides", { path, value });
      setMap((m) => ({ ...m, [path]: value }));
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
      throw e;
    }
  }, []);

  const clear = useCallback(async (path) => {
    if (!path) return;
    try {
      await api.delete("/playhq/overrides", { params: { path } });
      setMap((m) => {
        const { [path]: _, ...rest } = m;
        return rest;
      });
      toast.success("Reverted to source value");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revert");
    }
  }, []);

  return { map, loaded, get, save, clear, refresh };
}
